import { z } from 'zod';
import { insuranceClaimsScopeOwnerUnavailableReasonSchema } from './insurance-claims-documents-scope-completion.contract.js';
export const insuranceClaimsFinanceScopeBillingCapability = 'finance.insurance-claim-scope-billing.v1';
export const insuranceClaimsFinanceScopeBillingSlot = 'insurance-claims.scope-billing';
export const insuranceClaimsFinanceScopeBillingProviderId = 'finance.server.insurance-claims-scope-billing';
const idSchema = z.string().uuid();
const countSchema = z.number().int().nonnegative();
const centsSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const insuranceClaimsFinanceScopeBillingProviderRequestSchema = z.strictObject({
    consumerModuleId: z.literal('insurance-claims'),
    requestedCapability: z.literal(insuranceClaimsFinanceScopeBillingCapability),
    resourceType: z.literal('insurance-claims.scope'),
    resourceId: idSchema,
    claimId: idSchema,
    projectId: idSchema,
});
export const insuranceClaimsFinanceScopeInvoiceFactSchema = z.strictObject({
    capability: z.literal(insuranceClaimsFinanceScopeBillingCapability),
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
    invoiceId: idSchema,
    invoiceNumber: z.string().trim().min(1).max(40),
    status: z.enum(['draft', 'sent', 'partial', 'paid']),
    currencyCode: z.literal('USD'),
    totalCents: centsSchema,
    amountPaidCents: centsSchema,
    amountWrittenOffCents: centsSchema,
    amountDueCents: centsSchema,
    contentHash: sha256Schema,
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (value.totalCents - value.amountPaidCents - value.amountWrittenOffCents !==
        value.amountDueCents) {
        context.addIssue({ code: 'custom', message: 'Invoice balances must reconcile exactly.' });
    }
});
export const insuranceClaimsFinanceScopeBillingProviderResponseSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(insuranceClaimsFinanceScopeBillingCapability),
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
    currencyCode: z.literal('USD'),
    invoiceCount: countSchema,
    byStatus: z.strictObject({
        draft: countSchema, sent: countSchema, partial: countSchema, paid: countSchema,
    }),
    totalBilledCents: centsSchema,
    totalPaidCents: centsSchema,
    totalWrittenOffCents: centsSchema,
    totalDueCents: centsSchema,
    invoices: z.array(insuranceClaimsFinanceScopeInvoiceFactSchema).max(500),
    contentHash: sha256Schema,
    observedAt: z.string().datetime({ offset: true }).nullable(),
}).superRefine((value, context) => {
    const statusTotal = Object.values(value.byStatus).reduce((sum, count) => sum + count, 0);
    const invoiceIds = value.invoices.map(({ invoiceId }) => invoiceId);
    if (statusTotal !== value.invoiceCount || value.invoices.length !== value.invoiceCount ||
        new Set(invoiceIds).size !== invoiceIds.length) {
        context.addIssue({ code: 'custom', path: ['invoices'], message: 'Invoice facts must reconcile.' });
    }
    const totals = value.invoices.reduce((summary, invoice, index) => {
        if (invoice.claimId !== value.claimId || invoice.projectId !== value.projectId ||
            invoice.scopeId !== value.scopeId) {
            context.addIssue({ code: 'custom', path: ['invoices', index], message: 'Invoice identity mismatch.' });
        }
        return {
            billed: summary.billed + invoice.totalCents,
            paid: summary.paid + invoice.amountPaidCents,
            writtenOff: summary.writtenOff + invoice.amountWrittenOffCents,
            due: summary.due + invoice.amountDueCents,
        };
    }, { billed: 0, paid: 0, writtenOff: 0, due: 0 });
    if (totals.billed !== value.totalBilledCents || totals.paid !== value.totalPaidCents ||
        totals.writtenOff !== value.totalWrittenOffCents || totals.due !== value.totalDueCents) {
        context.addIssue({ code: 'custom', path: ['invoices'], message: 'Invoice totals mismatch.' });
    }
});
export const insuranceClaimsFinanceScopeBillingAvailabilitySchema = z.discriminatedUnion('state', [
    insuranceClaimsFinanceScopeBillingProviderResponseSchema,
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.literal(insuranceClaimsFinanceScopeBillingCapability),
        reasonCode: insuranceClaimsScopeOwnerUnavailableReasonSchema,
    }),
]);
//# sourceMappingURL=insurance-claims-finance-scope-billing.contract.js.map